import React, { useMemo, useEffect } from 'react';
import {
  BankIcon,
  Box,
  ButtonWithLink,
  CashIcon,
  CheckIcon,
  Center,
  Cluster,
  constants,
  DefaultPageTemplate,
  GenericCard,
  Title,
  Text,
} from '@thecb/components';
import AlertBar from '../../components/alert-bar';
import { Table } from '../../components/table';
import {
  formatPrice,
  capitalize,
  formatDate,
  titleCase,
} from '../../utils/helpers';
import { OrdersIndexFiltersForm } from './OrdersIndexFiltersForm';
import { CASH, CHECK, WIRE, ORDER_STATUS } from '../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrdersFilterOptionsRequest } from '../../state/shared';
import { getDefaultFormSelectOption } from '../../utils/filterFormsHelpers';

const Header = ({ alertBar, alertBarActions }) => (
  <Box width="100%" padding="1rem 0">
    <Center maxWidth="85.714rem">
      {alertBar.alerts.length > 0 && (
        <Box padding="1rem 0">
          <AlertBar {...alertBar} {...alertBarActions} />
        </Box>
      )}
      <Title
        color={constants.colors.CHARADE_GREY}
        weight={constants.fontWeights.FONT_WEIGHT_BOLD}
      >
        Orders
      </Title>
    </Center>
  </Box>
);

const renderIcon = (value) => {
  const paymentType = value?.[0];

  switch (paymentType) {
    case CHECK:
      return <BankIcon />;
    case WIRE:
      return <CheckIcon />;
    case CASH:
      return <CashIcon />;
    default:
      return <GenericCard />;
  }
};

const PaymentMethodCell = ({ value }) => {
  if (!value)
    return (
      <Text fontSize="1rem" extraStyles={`font-style: italic;`}>
        Not taken
      </Text>
    );

  switch (value.length) {
    case 0:
      return (
        <Text fontSize="1rem" extraStyles={`font-style: italic;`}>
          Not taken
        </Text>
      );
    case 1:
      return (
        <Cluster justify="start" align="center">
          {renderIcon(value)}
          <Box padding="0 0.5rem">
            {value[0] === 'wire_payment'
              ? 'Wire Transfer'
              : capitalize(value[0])}
          </Box>
        </Cluster>
      );
    default:
      return <Text fontSize="1rem">Multi-tender</Text>;
  }
};

const OrdersIndex = ({
  alertBars,
  actions,
  fetching,
  fetchData,
  forms,
  data,
  pageCount,
  totalOrders,
  clearSearchForm,
  shouldClear,
  errors,
}) => {
  const dispatch = useDispatch();
  const {
    agencies,
    batches,
    order_statuses,
    payment_methods,
    locations,
    terminals,
    users,
  } = useSelector((state) => state.shared.tableFilterOptions);
  const { isLoadingOrders } = useSelector((state) => state.ordersIndex);
  useEffect(() => {
    dispatch(fetchOrdersFilterOptionsRequest());
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'Order Number',
        accessor: 'order_number',
        Cell: ({ value }) => (
          <ButtonWithLink
            url={`/pos/order/${value}`}
            variant="smallGhost"
            text={value}
            extraStyles={`margin: 0.5rem 0; text-align: left; min-width: 0px;`}
            textExtraStyles="font-size: 1rem;"
            contentOverride
          >
            <Text fontSize={'1rem'} color={constants.colors.LUCKY_POINT}>
              {value}
            </Text>
          </ButtonWithLink>
        ),
        width: 80,
      },
      {
        Header: 'Batch Number',
        accessor: 'batch_number',
        width: 160,
      },
      {
        Header: 'Services',
        accessor: 'services',
        width: 200,
        Cell: ({ value: services }) => services.join(', '),
      },
      {
        Header: 'Date',
        accessor: 'date',
        width: 160,
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: 'Payment Method',
        accessor: 'payment_methods',
        width: 130,
        Cell: PaymentMethodCell,
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: 80,
        Cell: ({ value, row }) =>
          row.original.parent_id
            ? titleCase(ORDER_STATUS.REFUNDED)
            : titleCase(value),
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        Cell: ({ value, row }) => (
          <Text
            fontSize={'1rem'}
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
          >
            {formatPrice(value, !!row.original.parent_id)}
          </Text>
        ),
        width: 50,
      },
    ],
    [],
  );
  return (
    <DefaultPageTemplate
      maxWidth="85.714rem"
      header={
        <Header
          key="OrdersIndexHeader"
          alertBar={alertBars.ordersIndexAlertBars}
          alertBarActions={actions.alertBars.ordersIndexAlertBars}
        />
      }
      gutters="0"
      content={
        <Table
          errors={errors}
          totalItems={totalOrders}
          columns={columns}
          data={data?.orders || []}
          loading={isLoadingOrders}
          pageCount={pageCount}
          fetchData={fetchData}
          labelForTotal="Orders"
        >
          <OrdersIndexFiltersForm
            {...forms.ordersIndexFiltersForm}
            {...actions.forms.ordersIndexFiltersForm}
            showErrors={errors}
            agencyOptions={[
              getDefaultFormSelectOption('agencies'),
              ...agencies,
            ]}
            locationOptions={[
              getDefaultFormSelectOption('locations'),
              ...locations,
            ]}
            orderStatusOptions={[
              getDefaultFormSelectOption('statuses'),
              ...order_statuses,
            ]}
            paymentMethodOptions={[
              getDefaultFormSelectOption('methods'),
              ...payment_methods,
            ]}
            terminalOptions={[
              getDefaultFormSelectOption('terminals'),
              ...terminals,
            ]}
            userOptions={[getDefaultFormSelectOption('users'), ...users]}
            batchOptions={[getDefaultFormSelectOption('batches'), ...batches]}
            clear={clearSearchForm}
            shouldClear={shouldClear}
            fetchData={fetchData}
            loading={fetching}
            forms={forms}
          />
        </Table>
      }
    />
  );
};

export default OrdersIndex;
